/**
 * Simplified Chinese translations
 */
import type { TranslationKeys } from '../utils/i18n';

export const zhHans: TranslationKeys = {
  controls: {
    maxTime: '最大时间',
    maxTimeMinutes: '最大时间（分钟）',
    travelMode: '出行方式',
    speed: '速度',
    mapStyle: '地图样式',
    language: '语言',
    share: '分享',
    play: '播放',
    pause: '暂停',
    restart: '重新开始',
    loading: '加载中...',
    showControls: '显示控制',
    hideControls: '隐藏控制',
    expandControls: '展开控制',
    collapseControls: '折叠控制',
    selectLocationHint: '请在地图上选择一个位置开始',
  },
  travelModes: {
    driving: '驾车',
    walking: '步行',
    cycling: '骑行',
    transit: '公共交通',
  },
  speed: {
    verySlow: '0.1x（非常慢）',
    slow: '0.25x（慢）',
    half: '0.5x（一半）',
    normal: '1x（正常）',
    fast: '1.5x（快）',
    veryFast: '2x（非常快）',
  },
  mapStyles: {
    streets: '街道',
    satellite: '卫星',
  },
  languages: {
    en: 'English',
    'zh-Hans': '简体中文 (Simplified Chinese)',
    'zh-Hant': '繁體中文 (Traditional Chinese)',
    es: 'Español (Spanish)',
    fr: 'Français (French)',
    de: 'Deutsch (German)',
    ja: '日本語 (Japanese)',
    ko: '한국어 (Korean)',
  },
  addressInput: {
    label: '搜索地址',
    placeholder: '输入地址以计算时间地图',
    noResults: '未找到结果',
    loading: '加载建议中',
  },
  error: {
    retry: '重试',
    dismiss: '关闭错误',
    somethingWentWrong: '出了点问题。请刷新页面。',
    browserNotSupported: '您的浏览器不支持此应用程序。请使用现代浏览器。',
    usageLimitReached: '您已达到测试版限制的 5 次计算。感谢您的试用！如果您对此项目感兴趣，请私信我。',
  },
  progress: {
    minutes: '分钟',
  },
  share: {
    urlCopied: 'URL已复制到剪贴板！',
    shareUrl: '分享URL',
  },
  emailGate: {
    title: '登录时间地图测试版',
    subtitle: '输入您的电子邮件地址以接收登录链接',
    emailPlaceholder: 'your.email@example.com',
    emailLabel: '电子邮件地址',
    sendButton: '发送登录链接',
    sendingButton: '正在发送登录链接...',
    checkEmailTitle: '检查您的邮箱',
    checkEmailMessage: '我们已向 {email} 发送了登录链接',
    checkEmailInstruction: '点击电子邮件中的链接以继续使用时间地图。',
    checkEmailHelp: '没有收到邮件？请检查您的垃圾邮件文件夹，或使用其他电子邮件地址重试。',
    emailRequired: '请输入您的电子邮件地址',
    emailInvalid: '请输入有效的电子邮件地址',
    domainRestriction: '测试版访问仅允许 @gmail.com、@outlook.com 和 @qq.com 电子邮件地址。',
    domainNotAllowed: '测试版访问目前仅限于 Gmail、Outlook 和 QQ 电子邮件地址',
    typoSuggestion: '您是否想输入 @{domain}？',
    rateLimitExceeded: '请求过多。请稍等片刻后重试。',
    invalidEmailFormat: '电子邮件地址无效。请检查后重试。',
    failedToSend: '发送登录链接失败。请重试。',
    magicLinkFailed: '发送魔法链接失败：{error}',
    testMeButton: '测试',
    bannerText: '时间地图',
    betaTag: '测试版',
  },
  testCode: {
    title: '输入测试码',
    subtitle: '有测试码吗？在下面输入以开始',
    placeholder: '输入代码',
    button: '使用代码继续',
    haveCodeLink: '有测试码吗？',
    useEmailLink: '改用电子邮件',
    invalid: '测试码无效',
    expired: '此测试码已过期',
    limitReached: '此测试码已达到使用限制',
    optionalEmailTitle: '保存您的进度（可选）',
    optionalEmailSubtitle: '想保存您的工作吗？在下面输入您的电子邮件（或暂时跳过）',
    optionalEmailPlaceholder: 'your.email@example.com（可选）',
    skipButton: '暂时跳过',
    submitEmailButton: '继续',
  },
  usageIndicator: {
    template: '剩余{count}次计算',
    loading: '加载中...',
  },
  disclaimer: {
    loading: '加载中...',
    title: '重要声明',
    content: '本项目为测试版/实验性项目。所有地图数据均由第三方服务 Mapbox 提供，未经独立验证。地图可能包含不准确信息，不应用于关键决策。旅行时间计算仅为估算值，准确性亦未经验证。对于因使用本应用程序而产生的任何不准确、错误或后果，我们不承担任何责任。',
    personalStance: '地图边界和内容不代表个人立场。本应用使用的地图可能包含不准确信息，请注意甄别。',
    consent: '继续使用本应用程序即表示您已知晓并接受上述所有条款和免责声明。',
  },
  intro: {
    title: '探索您的可达范围',
    message: '从任意地点出发，在指定时间内能到达多远。\n\n通过地图动画直观展示出行时间范围，帮助您在选择居住地、工作地点或出行目的地时做出更明智的决策。',
    step1: '在地图上搜索或点击设置出发点',
    step2: '地图会用动画显示您的出行时间可达范围',
    step3: '设置出行时间和交通方式',
    step4: '点击播放，查看可达范围如何随时间扩展',
    step5: '生成分享链接，把这个可视化结果分享给其他人',
    next: '下一步',
    skip: '跳过',
    gotIt: '知道了',
    startTour: '开始引导',
  },
};

